import React from 'react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { formatOneDecimal, occupancyTone, weekNumberOfPeriodKey } from './loadCapacityFormat'

const Kpi = ({ testId, label, value, caption = '', tone = '', valueClassName = '', large = false }) => (
  <div className={large ? 'mdd__kpi mdd__kpi--large' : 'mdd__kpi'} data-testid={testId}>
    <div className='mdd__kpi-label' data-testid='kpi-label'>{label}</div>
    <div
      className={['mdd__kpi-value', tone ? 'mdd__tone--' + tone : '', valueClassName].filter(Boolean).join(' ')}
      data-testid='kpi-value'
      data-tone={tone || undefined}
    >
      {value}
    </div>
    {caption !== ''
      ? <div className='mdd__kpi-caption' data-testid='kpi-caption'>{caption}</div>
      : null}
  </div>
)

/**
 * Story 175, CU5 (S88) -- three headline indicators of the window (the first `window.weekCount` weeks
 * of the horizon) and a row of four for the window and the whole horizon. Every figure is read from
 * the §1 answer as it arrived (INV-I): nothing is added up, divided or counted here. The only colour
 * rule is `occupancyTone`, shared with the load-capacity block (RN60).
 */
const MachineDetailKpis = ({ detail }) => {
  const { t_, i18n } = useSmartTranslation()
  const locale = i18n ? i18n.language : undefined
  const { device, window: machineWindow, horizon } = detail
  const hasCalendar = device.calendarId !== null && device.calendarId !== undefined
  const notAvailable = t_('Not available')
  const hours = (value) => `${formatOneDecimal(value, locale)} h`
  const weekCount = machineWindow.weekCount

  const ratio = machineWindow.utilizationRatioPercent
  const ratioTone = occupancyTone(ratio)
  const available = machineWindow.availableCapacityHours
  const firstFreeWeek = weekNumberOfPeriodKey(horizon.firstWeekWithAvailableCapacityPeriodKey)

  return (
    <div className='mdd__kpis'>
      <div className='mdd__kpi-row mdd__kpi-row--3'>
        <Kpi
          large
          testId='machine-kpi-window-load'
          label={t_('Load {{count}} weeks', { count: weekCount })}
          value={hours(machineWindow.totalLoadHours)}
          caption={t_('{{count}} FOs in the horizon', { count: detail.fabricationOrderCount })}
        />
        <Kpi
          large
          testId='machine-kpi-oee-capacity'
          label={t_('OEE capacity / week')}
          value={hasCalendar ? hours(device.weeklyCapacityHours) : notAvailable}
          caption={hasCalendar ? t_('{{hours}} h calendar/week', { hours: formatOneDecimal(device.weeklyCalendarHours, locale) }) : t_('No calendar defined')}
        />
        <Kpi
          large
          testId='machine-kpi-utilization'
          label={t_('Utilization ratio')}
          value={ratio === null || ratio === undefined ? notAvailable : `${formatOneDecimal(ratio, locale)} %`}
          tone={ratioTone}
          caption={t_('next {{count}} weeks', { count: weekCount })}
        />
      </div>
      <div className='mdd__kpi-row mdd__kpi-row--4'>
        <Kpi
          testId='machine-kpi-window-available'
          label={t_('Available {{count}} weeks', { count: weekCount })}
          value={available === null || available === undefined ? notAvailable : hours(available)}
          valueClassName={available < 0 ? 'mdd__negative' : ''}
        />
        <Kpi testId='machine-kpi-saturated-weeks' label={t_('Saturated weeks')} value={String(horizon.saturatedWeekCount)} />
        <Kpi
          testId='machine-kpi-first-free-week'
          label={t_('First week with capacity')}
          value={firstFreeWeek === null ? t_('None (week)') : t_('W{{week}}', { week: firstFreeWeek })}
        />
        <Kpi testId='machine-kpi-horizon-load' label={t_('Load in the horizon')} value={hours(horizon.totalLoadHours)} />
      </div>
    </div>
  )
}

export default MachineDetailKpis
